import { PhoneFilled } from "@ant-design/icons";
import { IStoreHeaderDetails } from "../types/types";
import { capitalize, capitalizeFirst } from "../utils/functions";

// Font stacks. The families are loaded in public/index.html; the fallbacks keep
// the header legible if the webfont hasn't arrived yet.
const FONT_DISPLAY_SERIF = '"Fraunces", Georgia, "Times New Roman", serif';
const FONT_DISPLAY_BOLD =
  '"Bricolage Grotesque", "Helvetica Neue", Arial, sans-serif';
const FONT_BODY = '"Inter", system-ui, -apple-system, sans-serif';

// "restaurant" -> scan-to-order & reservations (soft serif, left card).
// "bold"       -> online-order & hms (heavy uppercase display sans, hero band).
export type OrgHeaderVariant = "restaurant" | "bold";

function buildAddressLine(store?: IStoreHeaderDetails): string {
  return [
    capitalizeFirst(store?.street1),
    capitalize(store?.street2),
    capitalize(store?.city),
    [capitalize(store?.state), store?.zipCode].filter(Boolean).join(" "),
  ]
    .filter((part) => part && part.trim().length > 0)
    .join(" · ");
}

export function OrgDataComponent({
  storeDetailsData,
  variant = "restaurant",
}: {
  storeDetailsData?: IStoreHeaderDetails;
  variant?: OrgHeaderVariant;
}) {
  const addressLine = buildAddressLine(storeDetailsData);
  const logoSrc = storeDetailsData?.logo ?? "/assets/images/generic-logo.svg";

  if (variant === "bold") {
    // Hero-band treatment for retail (online) and hotels (hms). Colors are
    // inherited from the wrapping header band (store brand colors), so we only
    // drive typography and scale here.
    return (
      <div style={{ fontFamily: FONT_BODY }}>
        <img
          src={logoSrc}
          alt="store-logo"
          className="h-11 w-11 rounded-lg object-cover"
        />
        <h1
          className="mt-3 text-4xl sm:text-5xl font-extrabold uppercase leading-[0.98] tracking-tight"
          style={{ fontFamily: FONT_DISPLAY_BOLD, textWrap: "balance" }}
        >
          {capitalize(storeDetailsData?.name)}
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm opacity-80">
          {addressLine && <span>{addressLine}</span>}
          {addressLine && storeDetailsData?.phone && (
            <span className="opacity-50">·</span>
          )}
          {storeDetailsData?.phone && (
            <span className="inline-flex items-center gap-1.5">
              <PhoneFilled />
              {storeDetailsData.phone}
            </span>
          )}
        </div>
      </div>
    );
  }

  // "restaurant" — soft serif name in a left-aligned card, for scan-to-order
  // and reservations.
  return (
    <div
      className="flex flex-row items-center gap-4"
      style={{ fontFamily: FONT_BODY }}
    >
      <img
        src={logoSrc}
        alt="store-logo"
        className="h-16 w-16 flex-none rounded-xl object-cover"
      />

      <div className="min-w-0">
        <h2
          className="text-2xl sm:text-3xl font-semibold leading-tight"
          style={{ fontFamily: FONT_DISPLAY_SERIF, letterSpacing: "-0.01em" }}
        >
          {capitalize(storeDetailsData?.name)}
        </h2>

        {addressLine && (
          <div className="mt-0.5 text-sm leading-snug opacity-70">
            {addressLine}
          </div>
        )}

        {storeDetailsData?.phone && (
          <div className="mt-1.5 flex items-center gap-1.5 text-sm font-semibold">
            <PhoneFilled />
            <span>{storeDetailsData.phone}</span>
          </div>
        )}
      </div>
    </div>
  );
}
